// v2/services/admin.service.js
//
// Cliente API para los endpoints administrativos v2:
//   - Panel "requiere revisión" (listar, resumen, marcar resuelto).
//   - Reintento manual de boletas y emails.
//   - Búsqueda de inscripciones por RUT/familiar/email (fix bug O11 Fresia).

import { v2Get, v2Post } from './client.js';

const BASE = '/v2/admin';

const AdminV2 = {
  // ---------- Panel revisión ----------

  /**
   * GET /v2/admin/revision/resumen
   * @returns {Promise<{pagosOnline, ventasPresenciales, boletasError, boletasParciales, total}>}
   */
  resumen: async (filtros = {}) => {
    // `desde` se manda para que los contadores sigan el mismo filtro que la tabla:
    // badges con el histórico sobre una tabla filtrada confunden más que ayudan.
    const params = {};
    if (filtros.desde) params.desde = filtros.desde;
    const r = await v2Get(`${BASE}/revision/resumen`, { params });
    return r.data || r;
  },

  /**
   * GET /v2/admin/revision
   * @param {object} [filtros]
   * @param {string} [filtros.tipo] 'pago_online' | 'venta_presencial' | 'boleta'
   * @param {string} [filtros.desde] ISO date string
   * @param {string} [filtros.hasta] ISO date string
   * @param {boolean} [filtros.incluirResueltos]
   * @param {number} [filtros.limit=100]
   * @returns {Promise<{total, items}>}
   */
  listarRevision: async (filtros = {}) => {
    const params = {};
    if (filtros.tipo) params.tipo = filtros.tipo;
    if (filtros.desde) params.desde = filtros.desde;
    if (filtros.hasta) params.hasta = filtros.hasta;
    if (filtros.incluirResueltos !== undefined) params.incluirResueltos = String(filtros.incluirResueltos);
    if (filtros.limit) params.limit = filtros.limit;
    const r = await v2Get(`${BASE}/revision`, { params });
    return r.data || r;
  },

  /**
   * GET /v2/admin/revision/:tipo/:id/detalle
   *
   * Contexto completo de un caso: la compra ítem por ítem con su inscripción y su
   * boleta, el cuadre de la plata (cobrado vs facturado) y las acciones sugeridas.
   * Va aparte del listado porque cargar esto en cada fila de la tabla sería caro.
   *
   * @param {string} tipo
   * @param {string} id
   * @returns {Promise<object>}
   */
  detalleCaso: async (tipo, id, subtipo) => {
    // `subtipo` importa en las filas de boleta: salen de dos colecciones distintas y
    // sin él el backend tiene que adivinar en cuál buscar.
    const r = await v2Get(`${BASE}/revision/${tipo}/${id}/detalle`, {
      params: subtipo ? { subtipo } : {}
    });
    return r.data?.data || r.data || r;
  },

  /**
   * POST /v2/admin/revision/boleta/:id/cerrar
   *
   * Cierra un caso de facturación SIN emitir boleta: el saldo se resolvió fuera de
   * Bsale (típicamente un reembolso por cobro duplicado). `marcarResuelto` no acepta
   * estas filas, así que sin esto la única acción posible era Reintentar — justo lo
   * que no había que hacer.
   *
   * @param {string} id
   * @param {object} body { motivo, subtipo? } — el motivo es obligatorio
   */
  cerrarCasoBoleta: async (id, body = {}) => {
    const r = await v2Post(`${BASE}/revision/boleta/${id}/cerrar`, body);
    return r.data || r;
  },

  /**
   * POST /v2/admin/revision/:tipo/:id/resolver
   * @param {string} tipo 'pago_online' | 'venta_presencial'
   * @param {string} id
   * @param {object} [body] { notas? }
   * @returns {Promise<{id, resueltoEn}>}
   */
  marcarResuelto: async (tipo, id, body = {}) => {
    const r = await v2Post(`${BASE}/revision/${tipo}/${id}/resolver`, body);
    return r.data || r;
  },

  /**
   * POST /v2/admin/revision/pago/:pagoId/completar-inscripcion
   *
   * Crea la inscripción que quedó pendiente en un pago YA cobrado. `idx` es el
   * índice del ítem, tal como lo publica el listado en `pendientes[].idx`.
   *
   * @param {string} pagoId
   * @param {object} body { idx, notas? }
   * @returns {Promise<{inscripcionId, sobrecupo, pendientesRestantes, boletaEncolada}>}
   */
  completarInscripcion: async (pagoId, body = {}) => {
    const r = await v2Post(`${BASE}/revision/pago/${pagoId}/completar-inscripcion`, body);
    return r.data || r;
  },

  // ---------- Reintentos manuales ----------

  /**
   * POST /v2/admin/boletas/reintentar/:tipo/:id
   * @param {string} tipo 'pago_online' | 'venta_presencial'
   * @param {string} id
   */
  reintentarBoleta: async (tipo, id) => {
    const r = await v2Post(`${BASE}/boletas/reintentar/${tipo}/${id}`, {});
    return r.data || r;
  },

  /**
   * POST /v2/admin/boletas/reencolar/:tipo/:id
   *
   * Devuelve a la cola un caso que agotó sus 5 intentos. `verificadoEnBsale` es
   * obligatorio en el backend: un timeout de Bsale no prueba que no haya emitido,
   * y re-emitir sobre un folio existente solo se revierte con nota de crédito.
   *
   * @param {string} tipo 'pago_online' | 'venta_presencial'
   * @param {string} id
   * @param {object} body { verificadoEnBsale, motivo? }
   */
  reencolarBoleta: async (tipo, id, body = {}) => {
    const r = await v2Post(`${BASE}/boletas/reencolar/${tipo}/${id}`, body);
    return r.data || r;
  },

  /**
   * POST /v2/admin/boletas/procesar-pendientes
   * @param {object} body { batch?, desde? }
   */
  procesarBoletasPendientes: async (body = {}) => {
    const r = await v2Post(`${BASE}/boletas/procesar-pendientes`, body);
    return r.data || r;
  },

  /**
   * POST /v2/admin/emails/procesar-pendientes
   * @param {object} body { limit? }
   */
  procesarEmailsPendientes: async (body = {}) => {
    const r = await v2Post(`${BASE}/emails/procesar-pendientes`, body);
    return r.data || r;
  },

  // ---------- Búsquedas ----------

  /**
   * GET /v2/admin/inscripciones?rut=|familiarId=|email=|usuarioId=
   * Resuelve el bug O11: ficha de un familiar muestra inscripciones aunque
   * las haya comprado el titular con otro cognitoId.
   *
   * @param {object} filtros - al menos uno: rut, familiarId, email, usuarioId
   * @returns {Promise<{total, inscripciones}>}
   */
  inscripcionesPorPersona: async (filtros = {}) => {
    const params = {};
    if (filtros.rut) params.rut = filtros.rut;
    if (filtros.familiarId) params.familiarId = filtros.familiarId;
    if (filtros.email) params.email = filtros.email;
    if (filtros.usuarioId) params.usuarioId = filtros.usuarioId;
    const r = await v2Get(`${BASE}/inscripciones`, { params });
    return r.data || r;
  }
};

export default AdminV2;
